import React, { MouseEvent, useCallback, useMemo, useState } from "react";
import { Form, Divider } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { toast } from "react-toastify";
import useAuthorizedContext from "hooks/useAuthorizedContext";

import FormTextInput from "../../molecules/FormFields/FormTextInput";
import useForm from "../../../hooks/useForm";
import FormGrid from "../../templates/FormGrid";
import Typography, {
  definedTypography,
} from "../../atoms/Typography/Typography";
import { definedRouteTypes } from "../../../api/api";
import FormRadioButton from "../../molecules/FormFields/FormRadioButton";
import SimpleButton, {
  definedButtonTypes,
} from "../../atoms/Buttons/SimpleButton/SimpleButton";
import { IMediaSourceForm } from "../../../api";
import FormTextArea from "../../molecules/FormFields/FormTextArea";
import FormImageZone from "../../molecules/FormFields/FormImageZone";
import useTypeTableContext, {
  definedTableActions,
} from "../../../hooks/useTypeTableContext";
import NewBackendModel from "../../../models/NewBackendModel";
import { updateMediaSource } from "../../../api/newBackendApi";
import FormSelect from "../../molecules/FormFields/FormSelect";
import { categoryOpts } from "../../../constants/categories";
import FormMultiSelect from "../../molecules/FormFields/FormMultiSelect";
import useLocalizedCountryOpts from "../../../hooks/useLocalizedCountryOpts";

interface MediaSourceFormProps {
  update?: boolean;
  initialValues?: Partial<IMediaSourceForm>;
  onFormClose?: (event?: MouseEvent) => void;
  type?: definedRouteTypes.videoNews;
}

const StyledDiv = styled.div({
  textAlign: "center",
});

const MediaSourceForm: React.FC<MediaSourceFormProps> = ({
  initialValues,
  onFormClose,
  update,
}) => {
  const CountryOptions = useLocalizedCountryOpts();
  const [isSubmitted, setSubmitted] = useState(update);
  const typeTableContext = useTypeTableContext();
  const { token } = useAuthorizedContext();

  const defaultInitValues = useCallback((): Partial<IMediaSourceForm> => {
    return {
      type: definedRouteTypes.SOURCE_MEDIA,
      category: undefined,
      countries: [],
      slug: "",
      url: "",
      title: "",
      imageUrl: undefined,
      seoDescription: "",
      show: true,

      blacklisted: false,
      automaticPosting: false,
    };
  }, []);
  const { t } = useTranslation();

  const submitFormQuery = async (form: Partial<IMediaSourceForm>) => {
    try {
      const act = update ? definedTableActions.update : definedTableActions.add;
      const postForm = await NewBackendModel(form);

      if (update) {
        const idToken = await token();
        await updateMediaSource(idToken, postForm);
      } else {
        throw new Error("create not supported yet, sowwy");
      }

      toast.success(
        t(update ? "Toasts.EditsSuccess" : "Toasts.Publish.mediju-avoti"),
      );
      if (typeTableContext) {
        typeTableContext.updateTableEntry(postForm as any, act);
      }
      if (onFormClose) onFormClose();
    } catch (err) {
      toast.error(t("Toasts.GenericError"));
      console.error("MediaSourceForm failed", err);
    }
  };

  const defaultInitialValues = useMemo(
    () => defaultInitValues(),
    [defaultInitValues],
  );

  const {
    values,
    submitForm,
    changeFormValue,
    isLoading,
    changeFormImageValue,
  } = useForm<Partial<IMediaSourceForm>>(
    initialValues || defaultInitialValues,
    submitFormQuery,
  );

  const {
    url,
    title,
    imageUrl,
    category,
    countries,
    seoDescription,
    blacklisted,
    automaticPosting,
    show,
  } = values;

  const formTitle = isSubmitted
    ? t("Forms.Titles.EditHeader")
    : t("Forms.Titles.AddHeader");
  const submit = isSubmitted
    ? t("Forms.Actions.Update")
    : t("Forms.Actions.Create");
  const submitAndClose = isSubmitted
    ? t("Forms.Actions.UpdateAndClose")
    : t("Forms.Actions.CreateAndClose");
  const convertedShow = Boolean(Number(show));

  return (
    <Form name="editorialNewsForm" loading={isLoading}>
      <Typography type={definedTypography.h1} as="span">
        {formTitle}
      </Typography>
      <Divider />
      <FormGrid label={t("Administrēšana")}>
        <FormRadioButton
          id="blacklisted"
          label="Blacklistē? (Šī Medija saites nekad netiek publicētas.)"
          value={blacklisted}
          onChange={changeFormValue}
        />
        <FormRadioButton
          id="automaticPosting"
          label="Automātiskā publicēšana? (Saite, kuru jebkurš ievieto no šī medija, automātiski parādās portālā)."
          value={automaticPosting}
          onChange={changeFormValue}
        />
        <FormRadioButton
          id="show"
          label="Rādīt portālā? (Pats Medijs ir izcelts mediju sarakstā)"
          value={convertedShow}
          onChange={changeFormValue}
        />
      </FormGrid>
      <FormGrid label={t("Forms.Titles.Content")}>
        <FormTextInput
          id="title"
          required
          value={title}
          label={t("Forms.Fields.title")}
          onChange={changeFormValue}
        />
        <FormTextInput
          id="url"
          value={url}
          label={t("Forms.Fields.url")}
          disabled
        />
        <FormImageZone
          id="imageUrl"
          required
          initialImgUrl={imageUrl}
          label={t("Forms.Fields.imagePointer")}
          onChange={changeFormValue}
          onImageChange={changeFormImageValue}
          description={t("Forms.Descriptions.eventsImage")}
        />
        <FormTextArea
          id="seoDescription"
          required
          label={t("Forms.Fields.seoDescription")}
          value={seoDescription}
          onChange={changeFormValue}
        />
      </FormGrid>
      <FormGrid label={t("Forms.Titles.Category")}>
        <FormSelect
          label={t("Forms.Fields.category")}
          id="category"
          value={category}
          options={categoryOpts}
          onChange={changeFormValue}
          search
        />
        <FormMultiSelect
          id="countries"
          label={t("Forms.Fields.countries")}
          options={CountryOptions}
          value={countries || []}
          onChange={changeFormValue}
          search
        />
      </FormGrid>
      <StyledDiv>
        {onFormClose && (
          <SimpleButton
            id="FormCancel"
            variant={definedButtonTypes.secondary}
            onClick={onFormClose}
          >
            {t("Forms.Actions.Cancel")}
          </SimpleButton>
        )}
        <SimpleButton
          id="FormSubmit"
          onClick={async (event) => {
            // TODO this is quickfix to enable updating...
            const response = await submitForm(event);
            setSubmitted(true);
            changeFormValue("slug", response ? response.slug : "");
          }}
          variant={definedButtonTypes.secondary}
        >
          {submit}
        </SimpleButton>
        <SimpleButton
          id="FormSubmitAndClose"
          onClick={async (event) => {
            await submitForm(event);
            if (onFormClose) onFormClose();
          }}
          variant={definedButtonTypes.primary}
        >
          {submitAndClose}
        </SimpleButton>
      </StyledDiv>
    </Form>
  );
};

MediaSourceForm.defaultProps = {
  type: definedRouteTypes.videoNews,
};

export default MediaSourceForm;
